import { BigNumber } from 'ethers';
import { formatEther, parseEther } from 'ethers/lib/utils';
import { useMemo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import useMarkets from '../../contexts/MarketsContext';
import useModal from '../../contexts/ModalContext';
import useTradeLogic from '../../contexts/TradeLogicContext';
import useUser from '../../contexts/UserContext';
import useWeb3 from '../../contexts/Web3Context';
import { HydratedPosition, hydratePositionData } from '../../utils/data';
import { formatDollarValue } from '../../utils/general';
import { ButtonS } from '../Button';
import { FillBar, FillCircle } from '../DynamicIcons';
import { ClosePositionModalContent } from '../Modal/ClosePositionModal';
import { Label3, Value1, Value4, Value5 } from '../Text';
import { Tooltip } from '../Tooltip';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    align-items: flex-start;
  }
`;

const HeaderChangeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;

  span {
    width: 1rem;
    margin: auto;
  }

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    flex-direction: column;
    gap: 0.25rem;

    span {
      margin-left: 1.5rem;
      transform: rotate(90deg);
    }
  }
`;

const ChangeItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

const DirectionLabel = styled(Value5)<{ positionSize: number; isChangingPosition?: boolean }>`
  background: ${({ theme, positionSize }) => {
    if (positionSize > 0) {
      return theme.greenAlpha;
    }
    if (positionSize < 0) {
      return theme.redAlpha;
    }
    return theme.gray1;
  }};
  color: ${({ theme, positionSize, isChangingPosition }) =>
    isChangingPosition || positionSize === 0 ? theme.gray5 : theme.white};
  padding: 0.375rem 0.5rem;
  border-radius: 0.5rem;
  margin-right: -0.375rem;
  min-width: 2.75rem;
  text-align: center;
`;

const StatsTable = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  gap: 1rem;
  max-height: 10rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  align-items: start;

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    max-height: unset;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 0.875rem;
  gap: 0.25rem;
  width: 40%;
  white-space: nowrap;

  @media only screen and (max-width: ${({ theme }) => theme.tablet}) {
    width: 100%;
  }
`;

const StatLabel = styled(Label3)`
  margin-right: auto;
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  align-items: center;
`;

const CloseButton = styled(ButtonS)`
  margin-left: auto;
`;

export function CurrentPosition() {
  const {
    quoteInput,
    currentPosition,
    addedPositionSize,
    combinedOpenNotional,
    combinedPositionSize,
    liquidationPrice,
    newMargin,
  } = useTradeLogic();
  const { marginRatio, refreshGlobalState, forceNewTraderPosition, reserveValue } = useUser();
  const { t } = useTranslation();
  const { address, provider, chainConfig } = useWeb3();
  const { marketList, refreshMarketInfo, selectedMarket } = useMarkets();
  const { setModal, closeModal } = useModal();

  const [positionData, setPositionData] = useState<HydratedPosition>();

  const hasCurrentPosition = useMemo<boolean>(
    () =>
      currentPosition?.positionSize !== undefined &&
      !BigNumber.from(currentPosition.positionSize).eq(0),
    [currentPosition],
  );

  const isChangingPosition = useMemo<boolean>(
    () => quoteInput !== undefined && !quoteInput.eq(0),
    [quoteInput],
  );

  const openClosePositionModal = useCallback(() => {
    if (setModal && positionData && selectedMarket) {
      setModal({
        title: t('Close position'),
        content: (
          <ClosePositionModalContent
            market={selectedMarket}
            position={positionData}
            onTxFinish={() => {
              forceNewTraderPosition(positionData.marketId);
              refreshGlobalState();
              refreshMarketInfo();
              closeModal();
            }}
          />
        ),
      });
    }
  }, [
    selectedMarket,
    setModal,
    closeModal,
    refreshGlobalState,
    refreshMarketInfo,
    forceNewTraderPosition,
    positionData,
    t,
  ]);

  useEffect(() => {
    let subscribed = true;
    if (provider && reserveValue && currentPosition && selectedMarket && address) {
      hydratePositionData({
        position: currentPosition,
        reserveValue,
        minMargin: BigNumber.from(selectedMarket.minMargin),
        marketList,
        chainConfig,
        provider,
        userAddress: address,
      }).then((res) => {
        if (subscribed && res !== null) {
          setPositionData(res);
        }
      });
    }
    return () => {
      subscribed = false;
    };
  }, [provider, reserveValue, currentPosition, selectedMarket, address, marketList, chainConfig]);

  return (
    <Container>
      <HeaderContainer>
        <HeaderChangeContainer>
          <ChangeItem>
            <DirectionLabel
              positionSize={
                hasCurrentPosition && currentPosition ? +currentPosition.positionSize : 0
              }
              isChangingPosition={isChangingPosition}
            >
              {(() => {
                if (currentPosition && +currentPosition.positionSize > 0) return t('LONG');
                if (currentPosition && +currentPosition.positionSize < 0) return t('SHORT');
                return t('NONE');
              })()}
            </DirectionLabel>
            <Value1 value={hasCurrentPosition && !isChangingPosition ? 0 : null}>
              {currentPosition
                ? formatDollarValue(
                    BigNumber.from(currentPosition.positionSize).abs(),
                    18,
                    true,
                    selectedMarket?.baseDecimals,
                  )
                : 0}{' '}
              {selectedMarket?.base}
            </Value1>
          </ChangeItem>
          {!addedPositionSize.eq(0) && (
            <>
              <span>-&gt;</span>
              <ChangeItem>
                <DirectionLabel positionSize={+combinedPositionSize.toString()}>
                  {(() => {
                    if (+combinedPositionSize.toString() > 0) return t('LONG');
                    if (+combinedPositionSize.toString() < 0) return t('SHORT');
                    return t('NONE');
                  })()}
                </DirectionLabel>
                <Value1>
                  {formatDollarValue(
                    combinedPositionSize.abs(),
                    18,
                    true,
                    selectedMarket?.baseDecimals,
                  )}{' '}
                  {selectedMarket?.base}
                </Value1>
              </ChangeItem>
            </>
          )}
        </HeaderChangeContainer>
        {currentPosition && (
          <CloseButton onClick={openClosePositionModal}>{t('Close position')}</CloseButton>
        )}
      </HeaderContainer>
      <StatsTable>
        <StatItem>
          <StatLabel>{t('Notional value')}</StatLabel>
          <Value4 value={hasCurrentPosition && !isChangingPosition ? 0 : null}>
            {currentPosition?.openNotional
              ? formatDollarValue(BigNumber.from(currentPosition.openNotional).abs(), 18, false, 2)
              : '-'}
          </Value4>
          {!addedPositionSize.eq(0) && (
            <>
              -&gt;{' '}
              <Value4>
                {combinedOpenNotional
                  ? formatDollarValue(BigNumber.from(combinedOpenNotional).abs(), 18, false, 2)
                  : '-'}
              </Value4>
            </>
          )}
        </StatItem>
        <StatItem>
          <StatLabel>{t('Account leverage')}</StatLabel>
          <FillBar
            fill={
              marginRatio && selectedMarket
                ? Number(
                    formatEther(
                      parseEther('1').divWei(marginRatio.divWei(selectedMarket.minMargin)),
                    ),
                  )
                : 0
            }
          />
          <Value4 value={hasCurrentPosition && !isChangingPosition ? 0 : null}>
            {marginRatio && !marginRatio.eq(parseEther('1'))
              ? `${Number(formatEther(parseEther('1').divWei(marginRatio))).toFixed(1)}x`
              : '-'}
          </Value4>
          {!addedPositionSize.eq(0) && (
            <>
              -&gt;{' '}
              <FillBar
                fill={
                  newMargin && selectedMarket
                    ? Number(
                        formatEther(
                          parseEther('1').divWei(newMargin.divWei(selectedMarket.minMargin)),
                        ),
                      )
                    : 0
                }
              />
              <Value4>
                {newMargin
                  ? `${Number(formatEther(parseEther('1').divWei(newMargin))).toFixed(1)}x`
                  : t('Invalid')}
              </Value4>
            </>
          )}
        </StatItem>
        <StatItem>
          <StatLabel>{t('Average entry price')}</StatLabel>
          <Value4 value={hasCurrentPosition && !isChangingPosition ? 0 : null}>
            {currentPosition && !BigNumber.from(currentPosition.positionSize).eq(0)
              ? formatDollarValue(
                  BigNumber.from(currentPosition.openNotional)
                    .divWei(currentPosition.positionSize)
                    .abs(),
                  18,
                  false,
                  selectedMarket?.displayDecimals,
                )
              : '-'}
          </Value4>
          {!addedPositionSize.eq(0) && (
            <>
              -&gt;{' '}
              <Value4>
                {combinedOpenNotional && combinedPositionSize && !combinedPositionSize.eq(0)
                  ? formatDollarValue(
                      combinedOpenNotional.divWei(combinedPositionSize).abs(),
                      18,
                      false,
                      selectedMarket?.displayDecimals,
                    )
                  : '-'}
              </Value4>
            </>
          )}
        </StatItem>
        <StatItem>
          <StatLabel>{t('Liquidation price')}</StatLabel>
          <Value4 value={hasCurrentPosition && !isChangingPosition ? 0 : null}>
            {positionData?.liquidationPrice !== undefined
              ? formatDollarValue(
                  parseEther(positionData.liquidationPrice),
                  18,
                  false,
                  selectedMarket?.displayDecimals,
                )
              : '-'}
          </Value4>
          {!addedPositionSize.eq(0) && (
            <>
              -&gt;{' '}
              <Value4>
                {liquidationPrice && liquidationPrice.gt('0')
                  ? formatDollarValue(liquidationPrice, 18, false, selectedMarket?.displayDecimals)
                  : '-'}
              </Value4>
            </>
          )}
        </StatItem>
        <StatItem>
          <StatLabel>{t('Margin usage')}</StatLabel>
          <FillCircle
            fill={
              (marginRatio && selectedMarket?.minMargin
                ? Number(
                    formatEther(
                      parseEther('1')
                        .divWei(marginRatio)
                        .divWei(parseEther('1').divWei(selectedMarket.minMargin))
                        .mul('100'),
                    ),
                  )
                : 0) / 100
            }
          />
          <Value4 value={hasCurrentPosition && !isChangingPosition ? 0 : null}>
            {selectedMarket && marginRatio && !marginRatio.eq(parseEther('1'))
              ? `${Number(
                  formatEther(
                    parseEther('1')
                      .divWei(marginRatio)
                      .divWei(parseEther('1').divWei(selectedMarket.minMargin))
                      .mul(100),
                  ),
                ).toFixed(2)}%`
              : '-'}
          </Value4>
          {!addedPositionSize.eq(0) && (
            <>
              -&gt;{' '}
              <FillCircle
                fill={
                  (newMargin && selectedMarket?.minMargin
                    ? Number(
                        formatEther(
                          parseEther('1')
                            .divWei(newMargin)
                            .divWei(parseEther('1').divWei(selectedMarket.minMargin))
                            .mul('100'),
                        ),
                      )
                    : 0) / 100
                }
              />
              <Value4>
                {selectedMarket && newMargin
                  ? `${Number(
                      formatEther(
                        parseEther('1')
                          .divWei(newMargin)
                          .divWei(parseEther('1').divWei(selectedMarket.minMargin))
                          .mul(100),
                      ),
                    ).toFixed(2)}%`
                  : '-'}
              </Value4>
            </>
          )}
        </StatItem>
        <StatItem>
          <StatLabel>{t('Funding payments')}</StatLabel>
          <Value4
            value={
              hasCurrentPosition && positionData?.fundingPayments
                ? Number(positionData.fundingPayments)
                : null
            }
          >
            {positionData?.fundingPayments
              ? formatDollarValue(positionData.fundingPayments, 18, false, 2)
              : '-'}
          </Value4>
        </StatItem>
        <StatItem>
          <StatLabel>
            {t('Unrealized PnL')}
            <Tooltip size="s" position="bottom" offset={0.15}>
              {t(
                'Unrealized PnL is calculated using the current market price and may not be the exact value received when closing the position.',
              )}
            </Tooltip>
          </StatLabel>
          <Value4
            value={
              hasCurrentPosition && positionData?.unrealizedPnl
                ? Number(positionData.unrealizedPnl)
                : null
            }
          >
            {positionData?.unrealizedPnl
              ? formatDollarValue(positionData.unrealizedPnl, 18, false, 2)
              : '-'}
          </Value4>
        </StatItem>
      </StatsTable>
    </Container>
  );
}
